import { useEffect, useState } from 'react';
import type { PopupBanner } from './popupBanner.types';

export type ArtworkStatus = 'idle' | 'loading' | 'ready' | 'failed';

/** Must match the `md` breakpoint the DS slide's <picture> switches on. */
const DESKTOP_QUERY = '(min-width: 768px)';

const pickUrl = (banner: PopupBanner): string => {
  const prefersDesktop =
    typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches;
  return prefersDesktop ? banner.imageDesktop : banner.imageMobile;
};

/**
 * Preloads the artwork the browser will actually use, before the dialog mounts (FE-17).
 *
 * The reason it happens here rather than inside the dialog: the DS slide renders a broken-image
 * fallback panel on failure, which is right for a carousel slot and wrong for a modal — an empty
 * frame over a full-screen scrim is worse than showing nothing. Preloading means the caller can
 * decide not to open at all, so that fallback never renders.
 *
 * `failed` is a terminal answer for this banner: the caller must not open and must not record a
 * dismissal, so the user still gets the banner on a later visit.
 */
export const usePopupBannerArtwork = (banner: PopupBanner | null): ArtworkStatus => {
  const [status, setStatus] = useState<ArtworkStatus>('idle');
  const url = banner ? pickUrl(banner) : null;

  useEffect(() => {
    if (!url) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const img = new Image();
    img.onload = () => {
      if (!cancelled) setStatus('ready');
    };
    img.onerror = () => {
      if (!cancelled) setStatus('failed');
    };
    img.src = url;

    // A cached image can complete before the handlers attach.
    if (img.complete) {
      setStatus(img.naturalWidth === 0 ? 'failed' : 'ready');
    }

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [url]);

  return status;
};
